import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useOneEffect } from "../useOneEffect";
import {
  Box,
  Typography,
  Switch,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { grey, red, green } from "@mui/material/colors";
import axios from "axios";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExcelJS from "exceljs";
import { createChatSocket } from "../../utils/chatSocket";

const panelHeight = 200;
const switchstyle = {
  backgroundColor: grey[200],
  borderRadius: 3,
};

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 12,
  },
}));

const excelbtn = {
  width: 35,
  height: 30,
  border: "solid 1px yellow",
  borderRadius: "3px",
  cursor: "pointer",
  display: "block",
  zIndex: 10,
  "&:hover": {
    backgroundColor: green[300],
  },
};

export default function MissionHistoryPanel(props) {
  const storageKey = "MissionHistoryPanel";
  const [checked, setChecked] = useState(() => {
    if (typeof sessionStorage === "undefined") return true;
    return sessionStorage[storageKey] !== "false";
  });
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [memoInput, setMemoInput] = useState("");
  const [memoSubmitting, setMemoSubmitting] = useState(false);
  const [filterKind, setFilterKind] = useState("all"); // 'all' | 'order' | 'status' | 'memo'
  const [showHelp, setShowHelp] = useState(false);
  const [newlyAddedIds, setNewlyAddedIds] = useState(() => new Set());
  const highlightDurationMs = 2500;

  const addRowWithHighlight = useCallback((safeRow) => {
    const id = safeRow.snum;
    setList((prev) => [safeRow, ...prev]);
    setNewlyAddedIds((prev) => new Set(prev).add(id));
    setTimeout(() => {
      setNewlyAddedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return new Set(next);
      });
    }, highlightDurationMs);
  }, []);

  const ondsid =
    typeof sessionStorage !== "undefined"
      ? (sessionStorage.ondsid || "").trim()
      : "";

  const socketRef = useRef(null);

  // 인수인계 시 소켓으로 실시간 수신 후 목록 앞에 한 건 추가 (풀링 없음)
  useEffect(() => {
    if (!socketRef.current) socketRef.current = createChatSocket();
    const s = socketRef.current;
    const handler = ({ dsid, row } = {}) => {
      if (!row || (dsid && dsid !== ondsid)) return;
      const safeRow = {
        snum: row.snum ?? `handover-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        dsnum: row.dsnum ?? ondsid,
        mf_snum: row.mf_snum ?? null,
        seqnum: row.seqnum ?? null,
        kind: row.kind ?? "handover",
        mfeqp: row.mfeqp ?? "",
        mfdorg: row.mfdorg ?? "",
        mfcsgn: row.mfcsgn ?? "",
        content: row.content ?? "",
        ipdt: row.ipdt ?? "",
      };
      addRowWithHighlight(safeRow);
    };
    s.on("mfhistory_handover", handler);
    return () => s.off("mfhistory_handover", handler);
  }, [ondsid, addRowWithHighlight]);

  const fetchHistory = async () => {
    if (!ondsid || !props.apiAddr) {
      setList([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(
        `${props.apiAddr}/get/mfhistory?dsid=${encodeURIComponent(ondsid)}`,
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setList(data);
    } catch (e) {
      console.warn("mfhistory fetch failed", e);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (checked && ondsid) fetchHistory();
    else setList([]);
  }, [ondsid, checked, props.apiAddr, props.refreshTrigger, refreshTick]);

  useEffect(() => {
    const onRefresh = () => setRefreshTick((t) => t + 1);
    window.addEventListener("mfHistoryRefresh", onRefresh);
    return () => window.removeEventListener("mfHistoryRefresh", onRefresh);
  }, []);

  // 한 건 추가 시 DB 전체 조회 없이 목록 앞에 행만 추가 + 신규 행 강조
  useEffect(() => {
    const onRowAdded = (e) => {
      const { dsid, row } = e.detail || {};
      if (!row || (dsid && dsid !== ondsid)) return;
      const safeRow = {
        snum: row.snum ?? `t-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        dsnum: row.dsnum ?? ondsid,
        mf_snum: row.mf_snum ?? null,
        seqnum: row.seqnum ?? null,
        kind: row.kind ?? "memo",
        mfeqp: row.mfeqp ?? "",
        mfdorg: row.mfdorg ?? "",
        mfcsgn: row.mfcsgn ?? "",
        content: row.content ?? "",
        ipdt: row.ipdt ?? "",
      };
      addRowWithHighlight(safeRow);
    };
    window.addEventListener("mfHistoryRowAdded", onRowAdded);
    return () => window.removeEventListener("mfHistoryRowAdded", onRowAdded);
  }, [ondsid, addRowWithHighlight]);

  const handleChange = (e) => {
    const val = e.target.checked;
    setChecked(val);
    if (typeof sessionStorage !== "undefined") {
      sessionStorage[storageKey] = val ? "true" : "false";
    }
  };

  useOneEffect(() => {
    // 전체보기/숨기기 프롭처리
    if (props.fullswitch == null) return;
    const ox = props.fullswitch;
    setChecked(ox);
    if (typeof sessionStorage !== "undefined") {
      sessionStorage[storageKey] = ox ? "true" : "false";
    }
  }, [props.fullswitch]);

  const kindLabel = (k) =>
    k === "order" ? "지령" : k === "status" ? "상태" : k === "memo" ? "메모" : k === "support" ? "지원" : k === "handover" ? "인수인계" : k || "기타";

  const filteredList =
    filterKind === "all"
      ? list
      : list.filter((row) => (row.kind || "").toString() === filterKind);

  const handleAddMemo = async () => {
    if (memoSubmitting) return; // 중복 요청 방지
    const text = (memoInput || "").trim();
    if (!text || !ondsid || !props.apiAddr) {
      if (!ondsid) alert("재난을 선택해주세요.");
      return;
    }
    const url = `${props.apiAddr.replace(/\/$/, "")}/post/mfhistorymemo`;
    const payload = { dsid: ondsid, content: text };
    console.log("[메모입력] 전송", { url, dsid: ondsid, contentLength: text.length });
    setMemoSubmitting(true);
    try {
      const res = await axios.post(url, payload);
      const data = res.data;
      const isSuccess = data && (data.result === "success" || data === "success");
      if (isSuccess) {
        console.log("[메모입력] 저장 성공");
        setMemoInput("");
        const now = new Date();
        const ipdt = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
        const memoRow = {
          snum: `memo-${Date.now()}`,
          dsnum: ondsid,
          kind: "memo",
          mfeqp: "",
          mfdorg: "",
          mfcsgn: "",
          content: text,
          ipdt,
        };
        setList((prev) => [memoRow, ...prev]);
      } else {
        console.warn("[메모입력] 저장 실패", data);
        const msg = (data && data.message) ? data.message : "메모 추가에 실패했습니다.";
        alert(msg);
      }
    } catch (e) {
      console.warn("[메모입력] 요청 실패", e?.response?.status, e?.response?.data, e?.message);
      const msg = e.response?.data?.message || e.message || "메모 추가 중 오류가 발생했습니다.";
      alert(msg);
    } finally {
      setMemoSubmitting(false);
    }
  };

  const handleExcelExport = async () => {
    if (filteredList.length === 0) {
      alert("내보낼 이력이 없습니다.");
      return;
    }
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("지령상태이력");
      sheet.columns = [
        { header: "시간", key: "ipdt", width: 22 },
        { header: "구분", key: "kind", width: 8 },
        { header: "종류", key: "mfeqp", width: 12 },
        { header: "소속", key: "mfdorg", width: 20 },
        { header: "장비명", key: "mfcsgn", width: 16 },
        { header: "내용", key: "content", width: 40 },
      ];
      sheet.getRow(1).font = { bold: true };
      filteredList.forEach((row) => {
        sheet.addRow({
          ipdt: row.ipdt || "",
          kind: kindLabel(row.kind),
          mfeqp: row.mfeqp || "-",
          mfdorg: row.mfdorg || "-",
          mfcsgn: row.mfcsgn || "-",
          content: row.content || "-",
        });
      });
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "지령상태이력.xlsx";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("엑셀 변환 중 오류:", error);
      alert("엑셀 파일 생성 중 오류가 발생했습니다.");
    }
  };

  const isEnding = typeof sessionStorage !== "undefined" && sessionStorage.oningox != 1; // 재난 종료 중이면 회색 배경 (Status와 동일)

  const headerCompact = {
    padding: "4px 10px",
    minHeight: 36,
  };

  return (
    <Box
      id="missionHistoryPanel"
      sx={{
        position: "absolute",
        bottom: 16,
        left: "50%",
        transform: "translateX(-50%)",
        width: checked ? "65%" : "auto",
        minWidth: checked ? 416 : "unset",
        maxWidth: checked ? 832 : "unset",
        height: checked ? panelHeight + 48 : 36,
        zIndex: 10,
        backgroundColor: isEnding ? grey[300] : "background.paper",
        border: "1px solid black",
        borderRadius: "10px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.2)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 0,
          height: checked ? "40px" : 36,
          minHeight: checked ? "40px" : 36,
          maxHeight: checked ? "40px" : 36,
          backgroundColor: isEnding ? grey[500] : red[900],
          color: "white",
          fontWeight: "bold",
          boxSizing: "border-box",
          flexWrap: "nowrap",
          fontSize: "1.25rem",
          overflow: "hidden",
          lineHeight: 1,
          "& > *": { margin: 0 },
          ...(!checked ? headerCompact : {}),
        }}
      >
        <LightTooltip title={ondsid ? "이력 새로고침" : "재난을 선택하면 새로고침할 수 있습니다"} placement="bottom" arrow>
          <Box
            component="span"
            onClick={() => ondsid && fetchHistory()}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              cursor: ondsid ? "pointer" : "default",
              opacity: ondsid ? 1 : 0.6,
              "&:hover": ondsid ? { opacity: 0.9 } : {},
              mr: 0.5,
              flexShrink: 0,
            }}
          >
            <RefreshIcon sx={{ fontSize: 24 }} />
          </Box>
        </LightTooltip>
        <Typography
          variant="h6"
          sx={{
            color: "white",
            padding: 0,
            fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
            letterSpacing: 0.0075,
            flexShrink: 0,
            fontWeight: 700,
            fontSize: "1.25rem",
            margin: 0,
            lineHeight: 1.25,
            mr: 0.5,
          }}
        >
          지령/상태 이력
        </Typography>
        {checked && <FormControl size="small" sx={{ minWidth: 90, flexShrink: 0, margin: 0, "& .MuiInputBase-root": { minHeight: 0 } }}>
          <Select
            value={filterKind}
            onChange={(e) => setFilterKind(e.target.value)}
            displayEmpty
            sx={{
              height: 30,
              minHeight: 30,
              fontSize: "0.8rem",
              color: "white",
              backgroundColor: "rgba(255,255,255,0.2)",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.6)" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
              "& .MuiSvgIcon-root": { color: "white" },
            }}
          >
            <MenuItem value="all">전체</MenuItem>
            <MenuItem value="order">지령</MenuItem>
            <MenuItem value="status">상태</MenuItem>
            <MenuItem value="memo">메모</MenuItem>
            <MenuItem value="support">지원</MenuItem>
            <MenuItem value="handover">인수인계</MenuItem>
          </Select>
        </FormControl>}
        {checked && <Box sx={{ flex: 1, minWidth: 80 }} />}
        {checked && <Box sx={{ width: 300, maxWidth: "40%", flexShrink: 0 }}>
          <TextField
            size="small"
            placeholder="메모 입력"
            value={memoInput}
            onChange={(e) => setMemoInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddMemo();
              }
            }}
            fullWidth
            inputProps={{ "aria-label": "메모 입력" }}
            sx={{
              "& .MuiInputBase-root": {
                backgroundColor: "white",
                minHeight: 30,
                maxHeight: 30,
                borderRadius: 1,
              },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.5)" },
              "& .MuiOutlinedInput-input": { py: 0.25, fontSize: "0.8rem" },
            }}
          />
        </Box>}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, marginLeft: checked ? 0 : "auto" }}>
          {checked && (
            <Button
              size="small"
              variant="contained"
              onClick={handleAddMemo}
              disabled={memoSubmitting || !memoInput.trim()}
              sx={{
                minWidth: 48,
                py: 0.5,
                fontSize: "0.75rem",
                backgroundColor: grey[800],
                "&:hover": { backgroundColor: grey[700] },
              }}
            >
              추가
            </Button>
          )}
          <Switch
            checked={checked}
            onChange={handleChange}
            sx={{ ...switchstyle, ...(!checked ? { marginLeft: 0.5, marginRight: 0 } : {}) }}
            size="small"
          />
          {checked && <LightTooltip title="엑셀 저장" placement="bottom" arrow>
            <DownloadIcon sx={{ ...excelbtn, width: 28, height: 26 }} onClick={handleExcelExport} />
          </LightTooltip>}
          <LightTooltip title="사용 방법 안내" placement="bottom" arrow>
            <Box
              component="span"
              onClick={() => setShowHelp(true)}
              sx={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 27, height: 27, borderRadius: "50%",
                border: "1.5px solid rgba(255,255,255,0.65)",
                cursor: "pointer", color: "white", fontWeight: "bold", fontSize: "0.75rem",
                flexShrink: 0,
                "&:hover": { backgroundColor: "rgba(54, 200, 66, 0.15)" },
                transition: "background 0.15s",
              }}
            >?</Box>
          </LightTooltip>
        </Box>
      </Box>
      {checked && (
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
            px: 1,
            py: 0.5,
            backgroundColor: isEnding ? grey[200] : undefined,
          }}
        >
          {!ondsid ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
              재난을 선택하면 이력이 표시됩니다.
            </Typography>
          ) : loading ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
              로딩 중...
            </Typography>
          ) : filteredList.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
              {list.length === 0 ? "이력이 없습니다." : "선택한 종류의 이력이 없습니다."}
            </Typography>
          ) : (
            <List dense disablePadding sx={{ py: 0 }}>
              {filteredList.map((row) => (
                <ListItem
                  key={row.snum}
                  dense
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: 0.35,
                    minHeight: 30,
                    "&:last-of-type": { borderBottom: "none" },
                    ...(newlyAddedIds.has(row.snum)
                      ? {
                          backgroundColor: "#ffecb3",
                          transition: "background-color 0.6s ease-out",
                        }
                      : { transition: "background-color 0.6s ease-out" }),
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="caption" component="span" sx={{ lineHeight: 1.15 }}>
                        {row.ipdt || ""} [{kindLabel(row.kind)}]{" "}
                        {(row.kind || "").toString() === "memo"
                          ? (row.content || "-")
                          : `${row.mfeqp || "-"} / ${row.mfdorg || "-"} / ${row.mfcsgn || "-"} / ${row.content || "-"}`}
                      </Typography>
                    }
                    primaryTypographyProps={{ noWrap: false }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}

      {/* 도움말 모달 — portal로 body에 직접 렌더링 */}
      {showHelp && ReactDOM.createPortal(
        <div
          onClick={() => setShowHelp(false)}
          style={{
            position: "fixed", inset: 0,
            backgroundColor: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex", alignItems: "flex-start", justifyContent: "center",
            overflowY: "auto",
            padding: "65px 0 40px", // Navbar(65px) 아래 = 상황판 상단과 동일선
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: "#fff",
              borderRadius: 10,
              padding: "24px 30px 20px",
              maxWidth: 540,
              width: "94%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
              position: "relative",
              fontFamily: "'Noto Sans KR','Malgun Gothic',sans-serif",
            }}
          >
            <button
              onClick={() => setShowHelp(false)}
              style={{
                position: "absolute", top: 12, right: 14,
                background: "none", border: "none",
                fontSize: "1.3rem", cursor: "pointer", color: "#888",
              }}
            >✕</button>

            <h2 style={{ margin: "0 0 4px", color: "#b71c1c", fontSize: "1.1rem" }}>
              📋 지령/상태 이력 사용 안내
            </h2>
            <p style={{ margin: "0 0 16px", fontSize: "0.78rem", color: "#888" }}>
              현재 재난에 관련된 <b>지령·상태 변경·메모</b>를 시간순으로 기록하는 패널입니다.
            </p>

            <h3 style={{ margin: "0 0 8px", color: "#c62828", fontSize: "0.98rem", borderBottom: "2px solid #ef9a9a", paddingBottom: 6 }}>
              📡 이력 구분
            </h3>
            <ul style={{ margin: "0 0 4px", paddingLeft: 20, lineHeight: 1.9, fontSize: "0.88rem", color: "#333" }}>
              <li><b>지령</b> — 재난 지휘부에서 자원에 내린 동원 지령 기록</li>
              <li><b>상태</b> — 자원의 상태 변경 기록 (대기 → 이동 → 활동 → 원대복귀)</li>
              <li><b>메모</b> — 담당자가 수동으로 입력한 메모</li>
              <li><b>지원</b> — 타시도 지원 관련 처리 기록</li>
              <li><b>인수인계</b> — 동원자원 사용자(인계자→인수자) 인수인계 기록</li>
            </ul>

            <hr style={{ margin: "14px 0", borderColor: "#e0e0e0" }} />

            <h3 style={{ margin: "0 0 8px", color: "#c62828", fontSize: "0.98rem", borderBottom: "2px solid #ef9a9a", paddingBottom: 6 }}>
              🔍 필터 / 새로고침
            </h3>
            <ul style={{ margin: "0 0 4px", paddingLeft: 20, lineHeight: 1.9, fontSize: "0.88rem", color: "#333" }}>
              <li>헤더 좌측 <b>새로고침(↺)</b> 버튼으로 최신 이력을 불러옵니다.</li>
              <li><b>전체 / 지령 / 상태 / 메모 / 지원 / 인수인계</b> 드롭다운으로 원하는 종류만 필터링할 수 있습니다.</li>
              <li>이력은 <b>최신순(위→아래)</b>으로 표시됩니다.</li>
            </ul>

            <hr style={{ margin: "14px 0", borderColor: "#e0e0e0" }} />

            <h3 style={{ margin: "0 0 8px", color: "#1565c0", fontSize: "0.98rem", borderBottom: "2px solid #90caf9", paddingBottom: 6 }}>
              ✏️ 메모 입력
            </h3>
            <ul style={{ margin: "0 0 4px", paddingLeft: 20, lineHeight: 1.9, fontSize: "0.88rem", color: "#333" }}>
              <li>상단 <b>메모 입력</b> 창에 내용 입력 후 <b>[추가]</b> 버튼 또는 <kbd style={{ background: "#f0f0f0", padding: "1px 5px", borderRadius: 3, fontSize: "0.82rem" }}>Enter</kbd>를 누르면 메모가 등록됩니다.</li>
              <li>재난 중 특이사항, 현장 상황, 지휘 결정 사항 등을 기록하는 데 활용하세요.</li>
            </ul>

            <hr style={{ margin: "14px 0", borderColor: "#e0e0e0" }} />

            <h3 style={{ margin: "0 0 8px", color: "#2e7d32", fontSize: "0.98rem", borderBottom: "2px solid #a5d6a7", paddingBottom: 6 }}>
              📥 엑셀 저장
            </h3>
            <ul style={{ margin: "0 0 4px", paddingLeft: 20, lineHeight: 1.9, fontSize: "0.88rem", color: "#333" }}>
              <li>우측 <b>다운로드(⬇)</b> 아이콘을 클릭하면 현재 필터 기준의 이력이 <b>엑셀 파일</b>로 저장됩니다.</li>
              <li>저장 항목: 시간 / 구분 / 종류 / 소속 / 장비명 / 내용</li>
            </ul>

            <hr style={{ margin: "14px 0", borderColor: "#e0e0e0" }} />

            <p style={{ margin: 0, fontSize: "0.82rem", color: "#888", lineHeight: 1.9 }}>
              💡 패널 우측 <b>토글 스위치</b>로 패널을 접고 펼 수 있습니다.
            </p>
          </div>
        </div>,
        document.body
      )}
    </Box>
  );
}
